import { Container, Typography, Box, Card, CardMedia, Dialog, DialogContent, DialogTitle, IconButton, Button } from '@mui/material'
import React, { useState } from 'react'
import { PhotoCamera, Person, Close, LocationOn, Payment, Edit, Collections } from '@mui/icons-material'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import styles from './trackingHistoryStyles'
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineOppositeContent, TimelineSeparator } from '@mui/lab'

// Configurar iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

const TrackingHistory = ({guide}) => {
  const [selectedImage, setSelectedImage] = useState(null)
  const [selectedImageType, setSelectedImageType] = useState(null)
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [mapDialogOpen, setMapDialogOpen] = useState(false)
  const [mapCoordinates, setMapCoordinates] = useState(null)

  const isBase64Svg = (base64String) => {
    if (!base64String) return false
    try {
      const cleanBase64 = base64String.replace(/\s/g, '')
      const decoded = atob(cleanBase64)
      return decoded.trim().startsWith('<svg') || decoded.includes('<svg')
    } catch (e) {
      try {
        const decoded = atob(base64String)
        return decoded.trim().startsWith('<svg') || decoded.includes('<svg')
      } catch (e2) {
        return false
      }
    }
  }

  const getDecodedSvg = (base64String) => {
    if (!base64String) return null
    try {
      const cleanBase64 = base64String.replace(/\s/g, '')
      return atob(cleanBase64)
    } catch (e) {
      try {
        return atob(base64String)
      } catch (e2) {
        return null
      }
    }
  }

  const formatImageUrl = (imageUrl, imageType) => {
    // Para signature y collection, verificar si necesita el prefijo base64
    if ((imageType === 'signature' || imageType === 'collection') && imageUrl) {
      // Si ya tiene el prefijo, retornarlo tal cual
      if (imageUrl.startsWith('data:image/')) {
        return imageUrl
      }
      
      // Limpiar el base64 de espacios y saltos de línea
      const cleanBase64 = imageUrl.replace(/\s/g, '')
      
      // Si es SVG, usar el prefijo SVG
      if (isBase64Svg(cleanBase64)) {
        return `data:image/svg+xml;base64,${cleanBase64}`
      }
      
      // Si no es SVG, usar el prefijo JPEG por defecto
      return `data:image/jpeg;base64,${cleanBase64}`
    }
    return imageUrl
  }

  const handleImageClick = (imageUrl, imageType = 'evidence') => {
    if (imageUrl) {
      const formattedUrl = formatImageUrl(imageUrl, imageType)
      setSelectedImage(formattedUrl)
      setSelectedImageType(imageType)
      setImageDialogOpen(true)
    }
  }

  const handleCloseImageDialog = () => {
    setImageDialogOpen(false)
    setSelectedImage(null)
    setSelectedImageType(null)
  }

  const handleOpenMap = (coordinates) => {
    if (coordinates && coordinates.latitude && coordinates.longitude) {
      setMapCoordinates({
        lat: parseFloat(coordinates.latitude),
        lng: parseFloat(coordinates.longitude)
      })
      setMapDialogOpen(true)
    }
  }

  const handleCloseMapDialog = () => {
    setMapDialogOpen(false)
    setMapCoordinates(null)
  }

  return (
    <Container style={styles.mainContainer}>
      <Typography variant="h6" sx={styles.title}>Historial del paquete</Typography>
      <Timeline position="left">
        {guide?.guides_history?.map((entry, index) => (
          <TimelineItem key={index}>
            <TimelineOppositeContent color="text.secondary">
              <Container sx={styles.dateContainer}>
                <Typography variant="subtitle2" component="span">{entry.date}</Typography>
                <Typography variant="body2" component="span">{entry.time}</Typography>
              </Container>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot />
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Box sx={styles.timelineContent}>
                <Typography variant="body1" sx={styles.statusText}>
                  {typeof entry.status === 'object' && entry.status !== null 
                    ? entry.status.keyname || entry.status.type || 'Desconocido'
                    : entry.status || 'Desconocido'}
                </Typography>
                
                {entry.reason_return && (() => {
                  const statusText = typeof entry.status === 'object' && entry.status !== null 
                    ? entry.status.keyname || entry.status.type || 'Desconocido'
                    : entry.status || 'Desconocido';
                  const isCancelled = statusText === 'Cancelado';
                  
                  return (
                    <Box sx={styles.reasonReturnContainer}>
                      <Typography variant="caption" sx={styles.reasonReturnText}>
                        {isCancelled ? 'Razón de cancelación' : 'Razón de devolución'}: {entry.reason_return}
                      </Typography>
                    </Box>
                  );
                })()}
                
                {entry.delivery_man && (
                  <Box sx={styles.deliveryManContainer}>
                    <Box sx={styles.deliveryManIconContainer}>
                      <Person sx={styles.deliveryManIcon} />
                    </Box>
                    <Typography variant="caption" sx={styles.deliveryManText}>
                      Responsable: {entry.delivery_man}
                    </Typography>
                  </Box>
                )}

                {/* Procesar photos array y separar por tipo */}
                {(() => {
                  // Obtener todas las fotos del entry (photos array o evidence_images/evidence_image para compatibilidad)
                  let allPhotos = []
                  
                  if (entry.photos && Array.isArray(entry.photos)) {
                    // Nueva estructura: photos array
                    allPhotos = entry.photos
                  } else if (entry.evidence_images && Array.isArray(entry.evidence_images)) {
                    // Estructura antigua: evidence_images array
                    allPhotos = entry.evidence_images.map(img => ({
                      url: typeof img === 'string' ? img : img.url,
                      type: 'evidence'
                    }))
                  } else if (entry.evidence_image) {
                    // Estructura antigua: evidence_image singular
                    allPhotos = [{
                      url: entry.evidence_image,
                      type: 'evidence'
                    }]
                  }

                  // Separar por tipo
                  const evidencePhotos = allPhotos.filter(photo => 
                    !photo.type || photo.type === 'evidence'
                  )
                  const identificationPhotos = allPhotos.filter(photo => 
                    photo.type === 'identification'
                  )
                  const paymentPhotos = allPhotos.filter(photo => 
                    photo.type === 'payment'
                  )
                  const signaturePhotos = allPhotos.filter(photo => 
                    photo.type === 'signature'
                  )
                  const collectionPhotos = allPhotos.filter(photo => 
                    photo.type === 'collection'
                  )

                  return (
                    <>
                      {/* Evidencias fotográficas */}
                      {evidencePhotos.length > 0 && (
                  <Box sx={styles.evidenceContainer}>
                    <Box sx={styles.evidenceHeader}>
                      <Typography variant="caption" sx={styles.evidenceLabel}>
                              Evidencias fotográficas ({evidencePhotos.length})
                      </Typography>
                      {entry.evidence_date && (
                              <Box>
                                <Typography variant="caption" sx={{...styles.evidenceDate, marginBottom: '4px'}}>
                                  {entry.evidence_date}
                                </Typography>
                                {entry.time && (
                                  <Typography variant="caption" sx={{...styles.evidenceDate, marginLeft: '6px'}}>
                                    {entry.time}
                                  </Typography>
                                )}
                              </Box>
                      )}
                    </Box>
                    <Box sx={styles.imagesGrid}>
                            {evidencePhotos.map((photo, imgIndex) => {
                              const imageUrl = typeof photo === 'string' ? photo : photo.url
                              const photoType = typeof photo === 'string' ? 'evidence' : (photo.type || 'evidence')
                              return (
                        <Card 
                          key={imgIndex}
                          sx={styles.evidenceCard}
                                  onClick={() => handleImageClick(imageUrl, photoType)}
                        >
                          <CardMedia
                            component="img"
                                    image={imageUrl}
                            alt={`Evidencia ${typeof entry.status === 'object' && entry.status !== null ? (entry.status.keyname || entry.status.type || '') : entry.status} - ${imgIndex + 1}`}
                            sx={styles.evidenceImage}
                          />
                          <Box sx={styles.imageOverlay}>
                            <PhotoCamera sx={styles.overlayIcon} />
                          </Box>
                        </Card>
                              )
                            })}
                    </Box>
                  </Box>
                )}
                
                      {/* Identificaciones */}
                      {identificationPhotos.length > 0 && (
                  <Box sx={styles.evidenceContainer}>
                    <Box sx={styles.evidenceHeader}>
                      <Typography variant="caption" sx={styles.evidenceLabel}>
                              Identificaciones ({identificationPhotos.length})
                      </Typography>
                      {entry.evidence_date && (
                              <Box>
                                <Typography variant="caption" sx={styles.evidenceDate}>
                                  {entry.evidence_date}
                                </Typography>
                                {entry.time && (
                                  <Typography variant="caption" sx={{...styles.evidenceDate, marginLeft: '6px'}}>
                                    {entry.time}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                          <Box sx={styles.identificationImagesGrid}>
                            {identificationPhotos.map((photo, imgIndex) => {
                              const imageUrl = typeof photo === 'string' ? photo : photo.url
                              const photoType = typeof photo === 'string' ? 'identification' : (photo.type || 'identification')
                              return (
                                <Card 
                                  key={imgIndex}
                                  sx={styles.identificationCard}
                                  onClick={() => handleImageClick(imageUrl, photoType)}
                                >
                                  <CardMedia
                                    component="img"
                                    image={imageUrl}
                                    alt={`Identificación ${typeof entry.status === 'object' && entry.status !== null ? (entry.status.keyname || entry.status.type || '') : entry.status} - ${imgIndex + 1}`}
                                    sx={styles.evidenceImage}
                                  />
                                  <Box sx={styles.imageOverlay}>
                                    <Person sx={styles.overlayIcon} />
                                  </Box>
                                </Card>
                              )
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* Pagos */}
                      {paymentPhotos.length > 0 && (
                        <Box sx={styles.evidenceContainer}>
                          <Box sx={styles.evidenceHeader}>
                            <Typography variant="caption" sx={styles.evidenceLabel}>
                              Pagos ({paymentPhotos.length})
                            </Typography>
                            {entry.evidence_date && (
                              <Box>
                                <Typography variant="caption" sx={{...styles.evidenceDate, marginBottom: '4px'}}>
                                  {entry.evidence_date}
                                </Typography>
                                {entry.time && (
                                  <Typography variant="caption" sx={{...styles.evidenceDate, marginLeft: '6px'}}>
                                    {entry.time}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                          <Box sx={styles.imagesGrid}>
                            {paymentPhotos.map((photo, imgIndex) => {
                              const imageUrl = typeof photo === 'string' ? photo : photo.url
                              const photoType = typeof photo === 'string' ? 'payment' : (photo.type || 'payment')
                              const paymentComment = typeof photo === 'object' && photo.payment_commentary ? photo.payment_commentary : null
                              return (
                                <Box key={imgIndex} sx={styles.paymentItemContainer}>
                    <Card 
                      sx={styles.evidenceCard}
                                    onClick={() => handleImageClick(imageUrl, photoType)}
                    >
                      <CardMedia
                        component="img"
                                      image={imageUrl}
                                      alt={`Pago ${typeof entry.status === 'object' && entry.status !== null ? (entry.status.keyname || entry.status.type || '') : entry.status} - ${imgIndex + 1}`}
                        sx={styles.evidenceImage}
                      />
                      <Box sx={styles.imageOverlay}>
                                      <Payment sx={styles.overlayIcon} />
                      </Box>
                    </Card>
                                  {paymentComment && (
                                    <Typography variant="caption" sx={styles.paymentComment}>
                                      {paymentComment}
                                    </Typography>
                                  )}
                                </Box>
                              )
                            })}
                          </Box>
                  </Box>
                )}

                      {/* Firmas */}
                      {signaturePhotos.length > 0 && (
                        <Box sx={styles.evidenceContainer}>
                          <Box sx={styles.evidenceHeader}>
                            <Typography variant="caption" sx={styles.evidenceLabel}>
                              Firmas ({signaturePhotos.length})
                            </Typography>
                            {entry.evidence_date && (
                              <Box>
                                <Typography variant="caption" sx={{...styles.evidenceDate, marginBottom: '4px'}}>
                                  {entry.evidence_date}
                                </Typography>
                                {entry.time && (
                                  <Typography variant="caption" sx={{...styles.evidenceDate, marginLeft: '6px'}}>
                                    {entry.time}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                          <Box sx={styles.imagesGrid}>
                            {signaturePhotos.map((photo, imgIndex) => {
                              const imageUrl = typeof photo === 'string' ? photo : photo.url
                              const photoType = typeof photo === 'string' ? 'signature' : (photo.type || 'signature')
                              const formattedImageUrl = formatImageUrl(imageUrl, photoType)
                              const isSvg = isBase64Svg(imageUrl)
                              const svgContent = isSvg ? getDecodedSvg(imageUrl) : null
                              
                              return (
                                <Card 
                                  key={imgIndex}
                                  sx={styles.evidenceCard}
                                  onClick={() => handleImageClick(imageUrl, photoType)}
                                >
                                  {isSvg && svgContent ? (
                                    <Box
                                      sx={{
                                        ...styles.evidenceImage,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '10px',
                                        backgroundColor: '#fff'
                                      }}
                                      dangerouslySetInnerHTML={{ __html: svgContent }}
                                    />
                                  ) : (
                                    <CardMedia
                                      component="img"
                                      image={formattedImageUrl}
                                      alt={`Firma ${typeof entry.status === 'object' && entry.status !== null ? (entry.status.keyname || entry.status.type || '') : entry.status} - ${imgIndex + 1}`}
                                      sx={styles.evidenceImage}
                                    />
                                  )}
                                  <Box sx={styles.imageOverlay}>
                                    <Edit sx={styles.overlayIcon} />
                                  </Box>
                                </Card>
                              )
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* Colecciones */}
                      {collectionPhotos.length > 0 && (
                        <Box sx={styles.evidenceContainer}>
                          <Box sx={styles.evidenceHeader}>
                            <Typography variant="caption" sx={styles.evidenceLabel}>
                              Colecciones ({collectionPhotos.length})
                            </Typography>
                            {entry.evidence_date && (
                              <Box>
                                <Typography variant="caption" sx={{...styles.evidenceDate, marginBottom: '4px'}}>
                                  {entry.evidence_date}
                                </Typography>
                                {entry.time && (
                                  <Typography variant="caption" sx={{...styles.evidenceDate, marginLeft: '6px'}}>
                                    {entry.time}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                          <Box sx={styles.imagesGrid}>
                            {collectionPhotos.map((photo, imgIndex) => {
                              const imageUrl = typeof photo === 'string' ? photo : photo.url
                              const photoType = typeof photo === 'string' ? 'collection' : (photo.type || 'collection')
                              const formattedImageUrl = formatImageUrl(imageUrl, photoType)
                              return (
                                <Card 
                                  key={imgIndex}
                                  sx={styles.evidenceCard}
                                  onClick={() => handleImageClick(imageUrl, photoType)}
                                >
                                  <CardMedia
                                    component="img"
                                    image={formattedImageUrl}
                                    alt={`Colección ${typeof entry.status === 'object' && entry.status !== null ? (entry.status.keyname || entry.status.type || '') : entry.status} - ${imgIndex + 1}`}
                                    sx={styles.evidenceImage}
                                  />
                                  <Box sx={styles.imageOverlay}>
                                    <Collections sx={styles.overlayIcon} />
                                  </Box>
                                </Card>
                              )
                            })}
                          </Box>
                        </Box>
                      )}
                    </>
                  )
                })()}

                {/* Mensaje cuando no hay evidencias */}
                {/* {!entry.evidence_images && !entry.evidence_image && (
                  <Box sx={styles.deliveryManContainer}>
                    <Box sx={styles.deliveryManIconContainer}>
                      <PhotoCamera sx={styles.deliveryManIcon} />
                    </Box>
                    <Typography variant="caption" sx={styles.deliveryManText}>
                      No se adjuntó evidencia fotográfica
                    </Typography>
                  </Box>
                )} */}

                {/* Botón para mostrar ubicación en mapa */}
                {entry.location && entry.location.latitude && entry.location.longitude && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LocationOn />}
                    onClick={() => handleOpenMap(entry.location)}
                    sx={styles.mapButton}
                  >
                    Ver ubicación
                  </Button>
                )}
              </Box>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>

      {/* Dialog para mostrar imagen en tamaño completo */}
      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={styles.dialogTitle}>
          {selectedImageType === 'identification' 
            ? 'Identificación' 
            : selectedImageType === 'payment' 
            ? 'Pago'
            : selectedImageType === 'signature'
            ? 'Firma'
            : selectedImageType === 'collection'
            ? 'Colección'
            : 'Evidencia Fotográfica'}
          <IconButton
            aria-label="cerrar"
            onClick={handleCloseImageDialog}
            sx={styles.closeButton}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (() => {
            // Extraer el base64 si tiene prefijo data:image
            let base64String = selectedImage
            if (selectedImage.startsWith('data:image/svg+xml;base64,')) {
              base64String = selectedImage.replace('data:image/svg+xml;base64,', '')
            } else if (selectedImage.startsWith('data:image/')) {
              base64String = selectedImage.split(',')[1] || selectedImage
            }
            
            const isSvg = selectedImageType === 'signature' && (selectedImage.includes('data:image/svg+xml') || isBase64Svg(base64String))
            const svgContent = isSvg ? getDecodedSvg(base64String) : null
            
            if (isSvg && svgContent) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    backgroundColor: '#fff',
                    minHeight: '400px'
                  }}
                  dangerouslySetInnerHTML={{ __html: svgContent }}
                />
              )
            }
            
            return (
              <CardMedia
                component="img"
                image={selectedImage}
                alt={
                  selectedImageType === 'identification' 
                    ? 'Identificación completa' 
                    : selectedImageType === 'payment'
                    ? 'Pago completo'
                    : selectedImageType === 'signature'
                    ? 'Firma completa'
                    : selectedImageType === 'collection'
                    ? 'Colección completa'
                    : 'Evidencia completa'
                }
                sx={styles.fullImage}
              />
            )
          })()}
        </DialogContent>
      </Dialog>

      {/* Dialog para mostrar mapa con ubicación */}
      <Dialog
        open={mapDialogOpen}
        onClose={handleCloseMapDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            maxWidth: { xs: '100%', sm: '90%', md: '90%' }, 
            height: { xs: '90%', sm: '80%', md: '80%' } 
          }
        }}
      >
        <DialogTitle sx={styles.dialogTitle}>
          Ubicación de la Evidencia
          <IconButton
            aria-label="cerrar"
            onClick={handleCloseMapDialog}
            sx={styles.closeButton}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {mapCoordinates && (
            <MapContainer 
              center={[mapCoordinates.lat, mapCoordinates.lng]} 
              zoom={15} 
              style={styles.mapContainer}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker position={[mapCoordinates.lat, mapCoordinates.lng]}>
                <Popup>Ubicación de la evidencia</Popup>
              </Marker>
            </MapContainer>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  )
}

export default TrackingHistory